import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaChevronDown,
  FaCircle,
  FaHome,
  FaLayerGroup,
} from "react-icons/fa";
import { setAuthToken } from "../../config/api";
import Logo from "../../assets/logo-dash.png";

if (localStorage.token) {
  setAuthToken(localStorage.token);
}

const getStorageMenus = () => {
  try {
    const menus = JSON.parse(localStorage.getItem("menus"));

    return Array.isArray(menus) ? menus : [];
  } catch (error) {
    return [];
  }
};

const isPathActive = (pathname, url) => {
  if (!url || url === "#") return false;

  return pathname === url || pathname.startsWith(`${url}/`);
};

const getModuleKey = (
  application,
  module,
  applicationIndex,
  moduleIndex
) =>
  `${
    application?.kategori_aplikasi_id ||
    application?.nama_aplikasi ||
    applicationIndex
  }-${module?.id || module?.slug_name || moduleIndex}`;

const getActiveModuleKeys = (storageItems, pathname) => {
  const openedModules = {};

  storageItems.forEach((application, applicationIndex) => {
    const modules = Array.isArray(application?.modules)
      ? application.modules
      : [];

    modules.forEach((module, moduleIndex) => {
      const menus = Array.isArray(module?.menus)
        ? module.menus
        : [];

      const moduleKey = getModuleKey(
        application,
        module,
        applicationIndex,
        moduleIndex
      );

      const hasActiveMenu = menus.some((menu) =>
        isPathActive(pathname, menu?.url)
      );

      if (hasActiveMenu) {
        openedModules[moduleKey] = true;
      }
    });
  });

  return openedModules;
};

export default function SidebarHome() {
  const navigate = useNavigate();
  const location = useLocation();

  const levelUser = localStorage.getItem("level");

  const storageItems = useMemo(() => {
    return getStorageMenus();
  }, []);

  const isMenuActive = (url) => {
    return isPathActive(location.pathname, url);
  };

  const isModuleAllowed = (moduleName = "") => {
    const normalizedName = moduleName.toLowerCase();

    if (
      normalizedName.includes("user privilege") ||
      normalizedName.includes("user previlege")
    ) {
      return levelUser === "developer";
    }

    return true;
  };

  const activeModuleKeys = useMemo(() => {
    return getActiveModuleKeys(
      storageItems,
      location.pathname
    );
  }, [location.pathname, storageItems]);

  const [openModules, setOpenModules] =
    useState(activeModuleKeys);

  useEffect(() => {
    setOpenModules((previous) => ({
      ...previous,
      ...activeModuleKeys,
    }));
  }, [activeModuleKeys]);

  const toggleModule = (moduleKey) => {
    setOpenModules((previous) => ({
      ...previous,
      [moduleKey]: !previous[moduleKey],
    }));
  };

  return (
    <aside className="sidebar sidebar-sims-concept">
      <div className="sidebar-sims-concept__inner">
        {/* LOGO */}
        <button
          type="button"
          className="sidebar-sims-concept__logo"
          onClick={() => navigate("/dashboard")}
          aria-label="Buka halaman utama"
        >
          <img src={Logo} alt="Logo SIMS" />
        </button>

        {/* AREA MENU */}
        <nav className="sidebar-sims-concept__navigation">
          {/* DASHBOARD */}
          <Link
            to="/dashboard"
            className={`sidebar-sims-concept__dashboard ${
              isMenuActive("/dashboard")
                ? "is-active"
                : ""
            }`}
          >
            <span className="sidebar-sims-concept__dashboard-icon">
              <FaHome />
            </span>

            <span>Halaman Utama</span>
          </Link>

          {/* MENU DINAMIS */}
          <div className="sidebar-sims-concept__applications">
            {storageItems.map(
              (application, applicationIndex) => {
                const modules = Array.isArray(
                  application?.modules
                )
                  ? application.modules.filter((module) =>
                      isModuleAllowed(module?.name)
                    )
                  : [];

                if (modules.length === 0) {
                  return null;
                }

                const applicationKey =
                  application?.kategori_aplikasi_id ||
                  application?.nama_aplikasi ||
                  `application-${applicationIndex}`;

                return (
                  <section
                    className="sidebar-sims-concept__application"
                    key={applicationKey}
                  >
                    {/* NAMA APLIKASI */}
                    <div className="sidebar-sims-concept__application-title">
                      {application?.nama_aplikasi ||
                        "Menu Aplikasi"}
                    </div>

                    {/* LIST MODULE */}
                    <div className="sidebar-sims-concept__module-list">
                      {modules.map(
                        (module, moduleIndex) => {
                          const menus = Array.isArray(
                            module?.menus
                          )
                            ? module.menus
                            : [];

                          const moduleKey =
                            getModuleKey(
                              application,
                              module,
                              applicationIndex,
                              moduleIndex
                            );

                          const moduleIsActive =
                            menus.some((menu) =>
                              isMenuActive(menu?.url)
                            );

                          const moduleIsOpen =
                            Boolean(
                              openModules[moduleKey]
                            );

                          if (menus.length === 0) {
                            return null;
                          }

                          return (
                            <div
                              className={`sidebar-sims-concept__module ${
                                moduleIsActive
                                  ? "is-active"
                                  : ""
                              }`}
                              key={moduleKey}
                            >
                              {/* BUTTON MODULE */}
                              <button
                                type="button"
                                className="sidebar-sims-concept__module-button"
                                onClick={() =>
                                  toggleModule(
                                    moduleKey
                                  )
                                }
                                aria-expanded={
                                  moduleIsOpen
                                }
                              >
                                <span className="sidebar-sims-concept__module-icon">
                                  {module?.icon_name ? (
                                    <i
                                      className={
                                        module.icon_name
                                      }
                                    />
                                  ) : (
                                    <FaLayerGroup />
                                  )}
                                </span>

                                <span className="sidebar-sims-concept__module-name">
                                  {module?.name ||
                                    "Menu"}
                                </span>

                                <span
                                  className={`sidebar-sims-concept__module-chevron ${
                                    moduleIsOpen
                                      ? "is-open"
                                      : ""
                                  }`}
                                >
                                  <FaChevronDown />
                                </span>
                              </button>

                              {/* SUBMENU */}
                              <div
                                className={`sidebar-sims-concept__submenu ${
                                  moduleIsOpen
                                    ? "is-open"
                                    : ""
                                }`}
                              >
                                <div className="sidebar-sims-concept__submenu-inner">
                                  {menus.map(
                                    (
                                      menu,
                                      menuIndex
                                    ) => {
                                      const menuUrl =
                                        menu?.url ||
                                        "#";

                                      const menuKey =
                                        menu?.id ||
                                        menu?.slug_name ||
                                        menu?.url ||
                                        `${moduleKey}-menu-${menuIndex}`;

                                      return (
                                        <Link
                                          key={
                                            menuKey
                                          }
                                          to={
                                            menuUrl
                                          }
                                          className={`sidebar-sims-concept__menu-item ${
                                            isMenuActive(
                                              menuUrl
                                            )
                                              ? "is-active"
                                              : ""
                                          }`}
                                        >
                                          <span className="sidebar-sims-concept__menu-dot">
                                            <FaCircle />
                                          </span>

                                          <span className="sidebar-sims-concept__menu-name">
                                            {menu?.name ||
                                              "Menu"}
                                          </span>
                                        </Link>
                                      );
                                    }
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </section>
                );
              }
            )}
          </div>
        </nav>
      </div>
    </aside>
  );
}